<!-- NOTICE: KAPTION-INTEGRATION -->
<script setup>
import { ref, watch, computed, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useKeyboardNavigableList } from 'dashboard/composables/useKeyboardNavigableList';
import AiSuggestionItem from '../conversation/AiSuggestionItem.vue';

const props = defineProps({
  items: {
    type: Array,
    default: () => [],
  },
  aiSuggestions: {
    type: Array,
    default: () => [],
  },
  isLoadingAi: {
    type: Boolean,
    default: false,
  },
  type: {
    type: String,
    default: 'canned',
  },
});

const emit = defineEmits(['mentionSelect', 'aiSelect']);

const { t } = useI18n();

const mentionsListContainerRef = ref(null);
const selectedIndex = ref(0);

// Combine regular items and AI suggestions
const allItems = computed(() => [
  ...props.items,
  ...props.aiSuggestions.map((suggestion, idx) => ({
    ...suggestion,
    isAi: true,
    aiIndex: idx,
  })),
]);

const adjustScroll = () => {
  nextTick(() => {
    const container = mentionsListContainerRef.value;
    if (!container) return;
    const selectedElement = container.querySelector(
      `#mention-item-${selectedIndex.value}`
    );
    if (selectedElement) {
      selectedElement.scrollIntoView({ block: 'nearest', behavior: 'auto' });
    }
  });
};

const onSelect = () => {
  const item = allItems.value[selectedIndex.value];
  if (item.isAi) {
    emit('aiSelect', item);
  } else {
    emit('mentionSelect', item);
  }
};

useKeyboardNavigableList({
  items: allItems,
  onSelect,
  adjustScroll,
  selectedIndex,
});

watch(
  () => allItems.value,
  newItems => {
    if (newItems.length < selectedIndex.value + 1) {
      selectedIndex.value = 0;
    }
  }
);

watch(selectedIndex, adjustScroll);

const onHover = index => {
  selectedIndex.value = index;
};

const onListItemSelection = index => {
  selectedIndex.value = index;
  onSelect();
};

const variableKey = (item = {}) => {
  return props.type === 'variable' ? `{{${item.label}}}` : `/${item.label}`;
};
</script>

<template>
  <div
    ref="mentionsListContainerRef"
    class="bg-n-solid-1 p-1 rounded-xl overflow-auto absolute w-full z-20 shadow-md left-0 bottom-full max-h-[400px] border border-solid border-n-strong mention--box"
  >
    <ul class="mb-0 vertical dropdown menu">
      <woot-dropdown-item
        v-for="(item, index) in allItems"
        :id="`mention-item-${index}`"
        :key="item.isAi ? `ai-${item.aiIndex}` : item.key"
        class="!mb-1"
        @mouseover="onHover(index)"
      >
        <button
          v-if="!item.isAi"
          class="flex rounded-lg group flex-col gap-0.5 overflow-hidden cursor-pointer items-start px-3 py-2 justify-center w-full h-full text-left hover:bg-n-alpha-black2"
          :class="{
            'bg-n-alpha-black2': index === selectedIndex,
          }"
          @click="onListItemSelection(index)"
        >
          <p
            class="max-w-full min-w-0 mb-0 overflow-hidden text-sm font-medium text-n-slate-11 group-hover:text-n-slate-12 text-ellipsis whitespace-nowrap"
            :class="{
              'text-n-slate-12': index === selectedIndex,
            }"
          >
            {{ item.description }}
          </p>
          <p
            class="max-w-full min-w-0 mb-0 overflow-hidden text-xs text-n-slate-11 group-hover:text-n-slate-12 text-ellipsis whitespace-nowrap"
            :class="{
              'text-n-slate-12': index === selectedIndex,
            }"
          >
            {{ variableKey(item) }}
          </p>
        </button>
        <div
          v-else
          class="flex rounded-lg group flex-col gap-0.5 overflow-hidden items-start px-3 py-2 justify-center w-full h-full text-left"
          :class="{
            'bg-n-alpha-black2': index === selectedIndex,
          }"
          @click="onListItemSelection(index)"
        >
          <AiSuggestionItem
            :suggestion="item"
            @select="onListItemSelection(index)"
          />
        </div>
      </woot-dropdown-item>
      <!-- Loading state for AI suggestions -->
      <li v-if="isLoadingAi" class="mb-1 list-none px-3 py-2">
        <div class="flex items-center gap-2">
          <div
            class="w-4 h-4 border-2 border-purple-600 border-t-transparent rounded-full animate-spin dark:border-purple-400 dark:border-t-transparent"
          />
          <span class="text-sm text-n-slate-11 dark:text-n-slate-9">
            {{ t('AI_SUGGESTIONS.LOADING') }}
          </span>
        </div>
      </li>
    </ul>
  </div>
</template>

<style scoped lang="scss">
.mention--box {
  .dropdown-menu__item:last-child > button {
    @apply border-0;
  }
}

.canned-item__button::v-deep .button__content {
  @apply overflow-hidden text-ellipsis whitespace-nowrap;
}
</style>
